/**
 * Day 3: Visual Calendar V2 — Calendar Summary Metrics Helper
 * Pure functions for deriving read-only summary data from loaded shifts.
 * 
 * Rules:
 * - Pure functions only — no API calls, no side effects
 * - No mutation of shift objects
 * - No permission or RBAC logic
 * - Uses scheduled times only (not actual worked hours)
 * - Coverage excludes CANCELLED shifts from denominator
 * - CONFIRMED is legacy but included in coverage if present in data
 */

/**
 * Parse HH:MM time string to minutes since midnight.
 * Returns null for invalid/missing times.
 */
function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const parts = timeStr.split(':');
  if (parts.length < 2) return null;
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * Calculate scheduled duration in hours between start and end time strings.
 * Handles overnight shifts (end < start → adds 24h).
 * Returns 0 for invalid inputs.
 */
function calcDurationHours(startTime, endTime) {
  const startMin = parseTimeToMinutes(startTime);
  const endMin = parseTimeToMinutes(endTime);
  if (startMin === null || endMin === null) return 0;
  let diff = endMin - startMin;
  if (diff <= 0) diff += 24 * 60; // overnight
  return diff / 60;
}

const COVERED_STATUSES = new Set([
  'PARTIALLY_FILLED', 'ASSIGNED', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'INVOICED',
]);
const EXCLUDED_FROM_COVERABLE = new Set(['CANCELLED']);

/**
 * Compute read-only summary metrics from an array of shift objects.
 * @param {Array} shifts - The loaded shifts array (not mutated)
 * @returns {Object} Summary metrics
 */
export function getCalendarSummaryMetrics(shifts) {
  if (!Array.isArray(shifts)) {
    return {
      total: 0, open: 0, partial: 0, assigned: 0,
      inProgress: 0, completed: 0, cancelled: 0,
      noShow: 0, invoiced: 0,
      coveragePercent: 0, totalScheduledHours: 0,
    };
  }

  let open = 0, partial = 0, assigned = 0, inProgress = 0;
  let completed = 0, cancelled = 0, noShow = 0, invoiced = 0;
  let coverable = 0, covered = 0;
  let totalMinutes = 0;

  for (const shift of shifts) {
    const status = shift.status || '';

    switch (status) {
      case 'OPEN': open++; break;
      case 'PARTIALLY_FILLED': partial++; break;
      case 'ASSIGNED': assigned++; break;
      case 'CONFIRMED': assigned++; break; // Legacy — count with assigned
      case 'IN_PROGRESS': inProgress++; break;
      case 'COMPLETED': completed++; break;
      case 'CANCELLED': cancelled++; break;
      case 'NO_SHOW': noShow++; break;
      case 'INVOICED': invoiced++; break;
      default: break;
    }

    // Coverage: coverable = all except CANCELLED
    if (!EXCLUDED_FROM_COVERABLE.has(status)) {
      coverable++;
      if (COVERED_STATUSES.has(status)) {
        covered++;
      }
    }

    // Scheduled hours
    const startMin = parseTimeToMinutes(shift.start_time);
    const endMin = parseTimeToMinutes(shift.end_time);
    if (startMin !== null && endMin !== null) {
      let diff = endMin - startMin;
      if (diff <= 0) diff += 24 * 60;
      totalMinutes += diff;
    }
  }

  const coveragePercent = coverable > 0 ? Math.round((covered / coverable) * 100) : 0;
  const totalScheduledHours = Math.round((totalMinutes / 60) * 10) / 10; // 1 decimal

  return {
    total: shifts.length,
    open,
    partial,
    assigned,
    inProgress,
    completed,
    cancelled,
    noShow,
    invoiced,
    coveragePercent,
    totalScheduledHours,
  };
}

/**
 * Extract unique filter options from loaded shifts.
 * Returns deduplicated, sorted arrays for dropdowns.
 * @param {Array} shifts - The loaded shifts array
 * @returns {Object} { staffOptions, participantOptions, serviceOptions }
 */
export function extractFilterOptions(shifts) {
  if (!Array.isArray(shifts)) {
    return { staffOptions: [], participantOptions: [], serviceOptions: [] };
  }

  const staffSet = new Map();
  const participantSet = new Map();
  const serviceSet = new Map();

  for (const shift of shifts) {
    // Staff from assigned_staff array
    if (shift.assigned_staff?.length > 0) {
      for (const s of shift.assigned_staff) {
        if (s.id && s.name) staffSet.set(s.id, s.name);
      }
    }
    // Staff from staff_name (enriched single assignment)
    if (shift.assigned_staff_id && shift.staff_name) {
      staffSet.set(shift.assigned_staff_id, shift.staff_name);
    }

    // Participants
    if (shift.participant_id && shift.participant_name) {
      participantSet.set(shift.participant_id, shift.participant_name);
    }

    // Services
    if (shift.service_catalog_id && shift.service_name) {
      serviceSet.set(shift.service_catalog_id, shift.service_name);
    }
  }

  const toSorted = (map) => Array.from(map.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    staffOptions: toSorted(staffSet),
    participantOptions: toSorted(participantSet),
    serviceOptions: toSorted(serviceSet),
  };
}

/**
 * Apply client-side filters to shifts array.
 * Filters stack with AND logic.
 * @param {Array} shifts
 * @param {Object} filters - { statusFilter, staffFilter, participantFilter, serviceFilter }
 * @returns {Array} Filtered shifts (new array, originals not mutated)
 */
export function applyClientFilters(shifts, filters) {
  if (!Array.isArray(shifts)) return [];

  const { statusFilter, staffFilter, participantFilter, serviceFilter } = filters;

  return shifts.filter(shift => {
    // Group sessions always pass staff/participant/service filters
    // (their data shape doesn't match individual shift fields)
    const isGS = shift._isGroupSession;

    // Status filter (preserve existing compound logic)
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'open') {
        if (shift.status !== 'OPEN' && shift.status !== 'PARTIALLY_FILLED') return false;
      } else if (statusFilter === 'assigned') {
        if (shift.status !== 'ASSIGNED' && shift.status !== 'CONFIRMED') return false;
      } else {
        if (shift.status !== statusFilter) return false;
      }
    }

    // Staff filter — GS passthrough
    if (staffFilter && !isGS) {
      const hasStaff = shift.assigned_staff?.some(s => s.id === staffFilter)
        || shift.assigned_staff_id === staffFilter;
      if (!hasStaff) return false;
    }

    // Participant filter — GS passthrough
    if (participantFilter && !isGS) {
      if (shift.participant_id !== participantFilter) return false;
    }

    // Service filter — GS passthrough
    if (serviceFilter && !isGS) {
      if (shift.service_catalog_id !== serviceFilter) return false;
    }

    return true;
  });
}
